import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Collapse,
  Paper,
  Tooltip
} from '@mui/material';
import { Reply as ReplyIcon, Close as CloseIcon } from '@mui/icons-material';
import axiosInstance from '../axiosInstance';

interface ReplyFormProps {
  feedbackId: number;
  onReplySuccess: () => void;
}

const ReplyForm: React.FC<ReplyFormProps> = ({ feedbackId, onReplySuccess }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!comment.trim()) return;

    setIsSubmitting(true);
    try {
      await axiosInstance.post(`/feedback/${feedbackId}/reply`, {
        comment: comment.trim()
      });
      setComment('');
      setIsOpen(false);
      onReplySuccess();
    } catch (error) {
      console.error('Error posting reply:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
      {!isOpen ? (
        <Tooltip title="Reply to this feedback" placement="top">
          <IconButton
            onClick={handleClick}
            size="small"
            sx={{
              color: 'primary.main',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'primary.main',
              ml: 0.5,
              '&:hover': {
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              },
              transition: 'all 0.2s ease-in-out',
              width: 32,
              height: 32
            }}
          >
            <ReplyIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      ) : (
        <Paper 
          sx={{ 
            p: 2, 
            bgcolor: 'background.secondary',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
              }}
              sx={{ 
                color: 'text.secondary',
                '&:hover': {
                  color: 'error.main'
                }
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              multiline
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Write your reply..."
              variant="outlined"
              size="small"
              sx={{ 
                mb: 1,
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper'
                }
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button
                variant="contained"
                type="submit"
                disabled={isSubmitting || !comment.trim()}
                size="small"
                sx={{
                  px: 3,
                  py: 0.5,
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Send Reply
              </Button>
            </Box>
          </form>
        </Paper>
      )}
    </Box>
  );
};

export default ReplyForm; 